import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import {
  IAnswer,
  IAnswerToQuestion,
  IAnswersGroup,
  IAnswersQuestion,
  INewQuestionnaireResponse,
  IQuestionnaireResponseSerializable,
  QRStatus,
} from "@gloria-public/questionnaire/domain/interfaces/questionnaire-response.interface";
import {
  EnableWhenBehavior,
  IQuestion,
  IQuestionnaireSerializable,
  QuestionnairePaging,
} from "@gloria-public/questionnaire/domain/interfaces/questionnaire.interface";
import { ISlideContentQuestion, ISlideView } from "@gloria-public/questionnaire/domain/interfaces/slide-view.interface";
import { QuestionnaireService } from "@gloria-public/questionnaire/domain/services/questionnaire.service";
import { TranslateService } from "@ngx-translate/core";
import { ICoding } from "@shared/interfaces/shared.interface";
import { ApiService } from "@shared/services/api.service";
import { SessionService } from "@shared/services/session.service";
import { Observable, Subject, combineLatest, first, firstValueFrom, map, race, startWith, takeUntil } from "rxjs";
import { QuestionnaireActionDialogComponent } from "./components/questionnaire-action-dialog/questionnaire-action-dialog.component";

@Component({
  selector: "app-questionnaire-page",
  templateUrl: "./questionnaire-page.component.html",
  styleUrls: ["./questionnaire-page.component.scss"],
})
export class QuestionnairePageComponent implements OnInit, OnDestroy {
  @ViewChildren("questionBlock") questionBlocks!: QueryList<ElementRef<HTMLElement>>;
  public questionnaire!: IQuestionnaireSerializable;
  public questionnaireResponse?: IQuestionnaireResponseSerializable;
  public slideViews: ISlideView[] = [];
  public currentSlideIndex = 0;
  public progressPercentage = 0;
  public isLoading = true;
  public allQuestionnaires: string[] = [];
  public currentQuestionnaireIndex = 0;
  public riskId!: string;
  public questionnaireId!: string;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  /** Emits when loadQuestionnaire is called and when the component is destroyed, used to complete all active subscriptions */
  private loadQuestionnaire$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private questionnaireService: QuestionnaireService,
    private translateService: TranslateService,
    private apiService: ApiService,
    public snackBar: MatSnackBar,
    private sessionService: SessionService,
    private dialog: MatDialog
  ) {
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state;

    if (state?.["allQuestionnaires"]) {
      this.allQuestionnaires = state["allQuestionnaires"];
    }
  }

  /**
   * Initializes the component, loads route parameters, and fetches questionnaire data.
   */
  ngOnInit(): void {
    this.scrollToTop();

    combineLatest([this.route.paramMap, this.translateService.onLangChange.pipe(startWith({ lang: this.translateService.currentLang }))])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([params, event]) => {
        const id = params.get("questionnaireId");

        if (!id) {
          this.router.navigate(["/not-found"]);
          return;
        }

        this.questionnaireId = id;
        const risk = params.get("riskId");

        if (!risk) {
          this.router.navigate(["/not-found"]);
          return;
        }
        this.riskId = risk;
        this.loadQuestionnaire(event.lang);
      });
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.loadQuestionnaire$.next();
    this.loadQuestionnaire$.complete();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Resets the questionnaire state to its initial default values.
   */
  private resetQuestionnaireState(): void {
    this.questionnaireResponse = undefined;
    this.slideViews = [];
    this.currentSlideIndex = 0;
    this.progressPercentage = 0;
    this.isLoading = true;
  }

  /**
   * Loads the questionnaire and initializes related state and responses.
   * @param lang Current language
   */
  private loadQuestionnaire(lang: string): void {
    // Cancel any previous in-flight call, including its async continuation
    this.loadQuestionnaire$.next();

    this.resetQuestionnaireState();
    if (this.allQuestionnaires.length > 0) {
      this.currentQuestionnaireIndex = this.allQuestionnaires.indexOf(this.questionnaireId);
    }

    // Capture the cancel signal for THIS specific call
    const cancel$ = this.loadQuestionnaire$.pipe(first());

    this.questionnaireService
      .getQuestionnaire(this.questionnaireId, lang)
      .pipe(first(), takeUntil(cancel$))
      .subscribe({
        next: (qr) => {
          if (qr) {
            this.questionnaire = qr;
            this.initializeQuestionnaireResponse(cancel$);
          }
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
          this.isLoading = false;
          this.router.navigate(["/landing"]);
        },
      });
  }

  /**
   * Initializes questionnaire responses and handles completed or in-progress states.
   * @param cancel$ Observable used to cancel ongoing operations when reloading.
   */
  private async initializeQuestionnaireResponse(cancel$: Observable<void>): Promise<void> {
    if (!this.sessionService.isLoggedIn && !this.sessionService.isGuest) {
      this.buildSlideViews();
      this.updateProgress();
      return;
    }

    // Helper: wraps firstValueFrom with a race against cancel$
    // Throws a specific symbol so we can distinguish cancellation from real errors
    const CANCELLED = Symbol("CANCELLED");
    const withCancel = <T>(obs: Observable<T>) =>
      firstValueFrom(
        race(
          obs.pipe(first()),
          cancel$.pipe(
            map(() => {
              throw CANCELLED;
            })
          )
        )
      );

    try {
      const completedResponses = await withCancel(
        this.questionnaireService.getQuestionnaireResponse(
          `${this.questionnaire.identifier.value}/${this.questionnaire.version}`,
          QRStatus.COMPLETED,
          this.questionnaire.date
        )
      );

      if (completedResponses && completedResponses.length > 0) {
        this.loadExistingResponse(completedResponses[0]); // for slideViews
        const NextStepsAfterQuestionnaire = await withCancel(
          this.questionnaireService.getNextStepsAfterQuestionnaire(completedResponses[0].identifier.value)
        );

        this.router.navigate(["/risks", this.riskId, "questionnaires", this.questionnaireId, "completed"], {
          state: {
            allQuestionnaires: this.allQuestionnaires,
            currentIndex: this.currentQuestionnaireIndex,
            hasNext: this.hasNextQuestionnaire,
            nextQuestionnaireId: this.nextQuestionnaireId,
            riskId: this.riskId,
            score: completedResponses[0].scoring.score,
            interpretation: completedResponses[0].scoring.interpretation,
            nextSteps: NextStepsAfterQuestionnaire.nextSteps,
            dna: NextStepsAfterQuestionnaire.dna,
            questionnaire: this.questionnaire,
            slideViews: this.slideViews,
          },
        });
        return;
      }

      const inProgressResponses = await withCancel(
        this.questionnaireService.getQuestionnaireResponse(
          `${this.questionnaire.identifier.value}/${this.questionnaire.version}`,
          QRStatus.IN_PROGRESS,
          this.questionnaire.date
        )
      );

      if (inProgressResponses && inProgressResponses.length > 0) {
        const action = await withCancel(
          this.dialog.open(QuestionnaireActionDialogComponent, { width: "450px", disableClose: true }).afterClosed()
        );

        if (action === "continue") {
          this.loadExistingResponse(inProgressResponses[0]);
        } else {
          await withCancel(this.questionnaireService.deleteQuestionnaireResponse(inProgressResponses[0].identifier.value));
          this.resetExistingResponse();
        }
      } else {
        this.buildSlideViews();
        this.updateProgress();
      }
    } catch (error) {
      if (error === CANCELLED) return; // silently abort, new call is already running

      this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
        duration: 5000,
      });
      this.router.navigate(["/landing"]);
    }
  }

  /**
   * Creates and persists a new questionnaire response for the current questionnaire.
   * @return The created questionnaire response object.
   */
  private async createNewQuestionnaireResponse(): Promise<IQuestionnaireResponseSerializable> {
    const newQR: INewQuestionnaireResponse = {
      questionnaire: {
        reference: `${this.questionnaire.identifier.value}/${this.questionnaire.version}`,
        display: this.questionnaire.group.title,
      },
      questionnairePublicationDate: this.questionnaire.date,
      subject: null,
    };

    await firstValueFrom(this.apiService.initGuestIfRequired());

    const response = await firstValueFrom(this.questionnaireService.addNewQuestionnaireResponse(newQR, this.riskId));

    this.questionnaireResponse = response;

    return response;
  }

  /**
   * Loads an existing questionnaire response and populates the UI state.
   * @param response Existing questionnaire response to load.
   */
  private loadExistingResponse(response: IQuestionnaireResponseSerializable): void {
    this.questionnaireResponse = response;
    this.buildSlideViews();
    this.fillExistingAnswers();
    this.updateProgress();
  }

  /**
   * Clears the current questionnaire response and rebuilds the questionnaire view.
   */
  private resetExistingResponse(): void {
    this.questionnaireResponse = undefined;
    this.buildSlideViews();
    this.updateProgress();
  }

  /**
   * Populates slide questions with answers from the existing questionnaire response.
   */
  private fillExistingAnswers(): void {
    this.slideViews.forEach((slide) => {
      slide.questions?.forEach((questionView) => {
        // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
        const existingAnswer = this.findExistingAnswerInResponse(this.questionnaireResponse!.group, questionView.question.linkId);

        if (existingAnswer && existingAnswer.answer && existingAnswer.answer.length > 0) {
          questionView.response.answer = [...existingAnswer.answer];
        }
      });
    });
  }

  /**
   * Recursively searches for an existing answer in a questionnaire response group.
   * @param group Questionnaire answer group to search within.
   * @param linkId Link identifier of the question to find.
   * @return The matching answer question or null if not found.
   */
  private findExistingAnswerInResponse(group: IAnswersGroup, linkId: string): IAnswersQuestion | null {
    if (group.question) {
      const found = group.question.find((q) => q.linkId === linkId);
      if (found) return found;
    }

    if (group.group) {
      for (const nestedGroup of group.group) {
        const found = this.findExistingAnswerInResponse(nestedGroup, linkId);
        if (found) return found;
      }
    }

    return null;
  }

  /**
   * Recursively clears all answers from the provided answer group.
   * @param group Questionnaire answer group to clear.
   */
  private clearAllAnswers(group: IAnswersGroup): void {
    if (group.question) {
      group.question.forEach((q) => {
        q.answer = [];
      });
    }

    if (group.group) {
      group.group.forEach((nestedGroup) => {
        this.clearAllAnswers(nestedGroup);
      });
    }
  }

  /**
   * Builds slide views based on the questionnaire paging configuration.
   */
  private buildSlideViews(): void {
    switch (this.questionnaire.paging) {
      case QuestionnairePaging.SINGLE:
        this.buildSingleQuestionPerSlide();
        break;
      case QuestionnairePaging.GROUP:
        this.buildGroupPerSlide();
        break;
      default:
        this.buildAllQuestionsInOneSlide();
        break;
    }

    this.isLoading = false;
  }

  /**
   * Creates one slide per questionnaire question.
   */
  private buildSingleQuestionPerSlide(): void {
    const questions = this.getAllQuestions();

    questions.forEach((question, index) => {
      const slideView: ISlideView = {
        title: question.groupTitle || this.questionnaire.group.title,
        description: question.groupDescription,
        questions: [
          {
            question: question.question,
            response: this.createEmptyResponse(question.question),
            groupTitle: question.groupTitle,
            groupDescription: question.groupDescription,
          },
        ],
      };
      this.slideViews.push(slideView);
    });
  }

  /**
   * Creates one slide per questionnaire group.
   */
  private buildGroupPerSlide(): void {
    if (this.questionnaire.group.group && this.questionnaire.group.group.length > 0) {
      // Multiple groups
      this.questionnaire.group.group.forEach((subGroup) => {
        const slideView: ISlideView = {
          title: subGroup.title,
          description: subGroup.text,
          questions: [],
        };

        subGroup.question?.forEach((question) => {
          slideView?.questions?.push({
            question: question,
            response: this.createEmptyResponse(question),
            groupTitle: subGroup.title,
            groupDescription: subGroup.text,
          });
        });

        this.slideViews.push(slideView);
      });
    } else {
      // Single group - same as NONE
      this.buildAllQuestionsInOneSlide();
    }
  }

  /**
   * Creates a single slide containing all questionnaire questions.
   */
  private buildAllQuestionsInOneSlide(): void {
    const slideView: ISlideView = {
      title: this.questionnaire.group.title,
      description: this.questionnaire.group.text,
      questions: [],
    };

    if (this.questionnaire.group.group && this.questionnaire.group.group.length > 0) {
      // Multiple groups but displayed together
      this.questionnaire.group.group.forEach((subGroup) => {
        subGroup.question?.forEach((question) => {
          slideView?.questions?.push({
            question: question,
            response: this.createEmptyResponse(question),
            groupTitle: subGroup.title,
            groupDescription: subGroup.text,
          });
        });
      });
    } else {
      // Flat structure
      this.questionnaire.group.question?.forEach((question) => {
        slideView?.questions?.push({
          question: question,
          response: this.createEmptyResponse(question),
          groupTitle: this.questionnaire.group.title,
          groupDescription: this.questionnaire.group.text,
        });
      });
    }

    this.slideViews.push(slideView);
  }

  /**
   * Retrieves all questions from the questionnaire with their group metadata.
   * @return Array of questions with associated group information.
   */
  private getAllQuestions(): Array<{ question: IQuestion; groupTitle?: string; groupDescription?: string }> {
    const allQuestions: Array<{ question: IQuestion; groupTitle?: string; groupDescription?: string }> = [];

    if (this.questionnaire.group.group && this.questionnaire.group.group.length > 0) {
      this.questionnaire.group.group.forEach((subGroup) => {
        subGroup.question?.forEach((question) => {
          allQuestions.push({
            question: question,
            groupTitle: subGroup.title,
            groupDescription: subGroup.text,
          });
        });
      });
    } else {
      this.questionnaire.group.question?.forEach((question) => {
        allQuestions.push({
          question: question,
          groupTitle: this.questionnaire.group.title,
          groupDescription: this.questionnaire.group.text,
        });
      });
    }

    return allQuestions;
  }

  /**
   * Creates an empty response object for a questionnaire question.
   * @param question Questionnaire question to initialize.
   * @return Initialized empty answer object.
   */
  private createEmptyResponse(question: IQuestion): IAnswersQuestion {
    return {
      linkId: question.linkId,
      text: question.text,
      imageUrls: question.imageUrls ? [question.imageUrls] : undefined,
      enableWhen: question.enableWhen,
      inputType: question.type,
      answer: [],
    };
  }

  /**
   * Gets the currently active slide view.
   * @return The current slide view.
   */
  get currentSlide(): ISlideView {
    return this.slideViews[this.currentSlideIndex];
  }

  /**
   * Updates the questionnaire progress percentage based on the current slide index.
   */
  private updateProgress(): void {
    if (this.slideViews.length > 0) {
      this.progressPercentage = ((this.currentSlideIndex + 1) / this.slideViews.length) * 100;
    }
  }

  /**
   * Navigates to the previous questionnaire slide.
   */
  previousSlide(): void {
    if (!(this.currentSlideIndex === 0)) {
      this.currentSlideIndex--;
      this.updateProgress();
      this.scrollToTop();
    }
  }

  /**
   * Navigates to the next questionnaire slide if validation passes.
   */
  nextSlide(): void {
    if (!this.canProceed()) {
      alert(this.translateService.instant("page.questionnaire.missingFields"));
      return;
    }

    if (!(this.currentSlideIndex === this.slideViews.length - 1)) {
      this.currentSlideIndex++;
      this.updateProgress();
      this.scrollToTop();
    }
  }

  /**
   * Scrolls the browser window to the top of the page.
   */
  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  /**
   * Determines whether the user can proceed from the current slide.
   * @return True if all required enabled questions are answered; otherwise false.
   */
  canProceed(): boolean {
    return this.currentSlide.questions
      ? this.currentSlide.questions.every((qView) => {
          if (!qView.question.required) return true;
          if (!this.isQuestionEnabled(qView.question, qView.response)) return true;
          return qView?.response?.answer && qView.response.answer.length > 0;
        })
      : false;
  }

  /**
   * Evaluates whether a question should be enabled based on enableWhen conditions.
   * @param question Question whose visibility should be evaluated.
   * @param response Current response associated with the question.
   * @return True if the question should be enabled; otherwise false.
   */
  isQuestionEnabled(question: IQuestion, response?: IAnswersQuestion): boolean {
    if (!question.enableWhen || question.enableWhen.length === 0) {
      return true;
    }

    const behavior = question.enableWhenBehavior || EnableWhenBehavior.AND;

    for (const enableWhen of question.enableWhen) {
      const dependentAnswer = this.findAnswer(enableWhen.question ?? "");
      if (!dependentAnswer || dependentAnswer.length === 0) {
        if (behavior === EnableWhenBehavior.AND) return false;
        continue;
      }

      const value = enableWhen.type === "number" ? Number(dependentAnswer[0].valueCoding.code) : dependentAnswer[0].valueCoding.code;

      const expectedValue = enableWhen.type === "number" ? Number(enableWhen.answer) : enableWhen.answer;

      let isEnabled = false;
      switch (enableWhen.operator) {
        case "=":
          isEnabled = value === expectedValue;
          break;
        case "!=":
          isEnabled = value !== expectedValue;
          break;
        case ">":
          isEnabled = value > expectedValue;
          break;
        case "<":
          isEnabled = value < expectedValue;
          break;
        case ">=":
          isEnabled = value >= expectedValue;
          break;
        case "<=":
          isEnabled = value <= expectedValue;
          break;
      }

      if (behavior === EnableWhenBehavior.OR && isEnabled) return true;
      if (behavior === EnableWhenBehavior.AND && !isEnabled) return false;
    }

    return behavior === EnableWhenBehavior.AND;
  }

  /**
   * Finds the answer for a question by its link identifier.
   * @param linkId Link identifier of the question to search for.
   * @return Array of answers if found; otherwise null.
   */
  private findAnswer(linkId: string): IAnswer[] | null {
    for (const slide of this.slideViews) {
      if (slide.questions) {
        const questionView = slide.questions.find((q) => q.question.linkId === linkId);
        if (questionView && questionView?.response?.answer) {
          return questionView.response.answer;
        }
      }
    }
    return null;
  }

  /**
   * Persists changes made to a questionnaire answer.
   * @param questionView Question view containing the updated response.
   */
  async onAnswerChanged(questionView: ISlideContentQuestion): Promise<void> {
    try {
      if (!this.questionnaireResponse) {
        await this.createNewQuestionnaireResponse();
      }
    } catch (error) {
      this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
        duration: 5000,
      });
      this.router.navigate(["/landing"]);
    }

    try {
      const answerPayload: IAnswerToQuestion = {
        // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
        questionnaireResponse: this.questionnaireResponse!.identifier.value,
        questionnaireDate: this.questionnaire.date,
        subject: null,
        status: QRStatus.IN_PROGRESS,
        linkID: questionView.question.linkId,
        answer:
          questionView?.response?.answer?.map((ans) => ({
            valueCoding: {
              code: ans.valueCoding.code,
              display: ans.valueCoding.display || null,
              system: ans.valueCoding.system || null,
            },
          })) ?? [],
      };

      await firstValueFrom(this.questionnaireService.answerToQuestion(answerPayload));
      this.goToNextQuestion(questionView);
    } catch (error) {
      this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
        duration: 5000,
      });
    }
  }

  private goToNextQuestion(currentQuestion: ISlideContentQuestion): void {
    if (!this.currentSlide.questions) return;

    // Only enabled questions
    const enabledQuestions = this.currentSlide.questions.filter((q) => this.isQuestionEnabled(q.question, q.response));

    const currentIndex = enabledQuestions.indexOf(currentQuestion);

    // Last question? Go to next slide.
    if (currentIndex === enabledQuestions.length - 1) {
      if (this.currentSlideIndex < this.slideViews.length - 1) {
        this.nextSlide();
      }
      return;
    }

    // Scroll to next question
    setTimeout(() => {
      const elements = this.questionBlocks.toArray();
      const nextElement = elements[currentIndex + 1];

      nextElement?.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }

  /**
   * Submits the questionnaire and marks the response as completed.
   */
  submitQuestionnaire(): void {
    if (!this.canProceed()) {
      alert(this.translateService.instant("page.questionnaire.missingFields"));
      return;
    }

    // Mark as completed
    const lastQuestion = this.currentSlide?.questions?.[this.currentSlide.questions.length - 1];
    const completePayload: IAnswerToQuestion = {
      // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
      questionnaireResponse: this.questionnaireResponse!.identifier.value,
      questionnaireDate: this.questionnaire.date,
      subject: null,
      status: QRStatus.COMPLETED,
      linkID: lastQuestion?.question?.linkId ?? "",
      answer:
        lastQuestion?.response?.answer?.map((ans) => ({
          valueCoding: {
            code: ans.valueCoding.code,
            display: ans.valueCoding.display || null,
            system: ans.valueCoding.system || null,
          },
        })) ?? [],
    };

    this.questionnaireService
      .answerToQuestion(completePayload)
      .pipe(first())
      .subscribe({
        next: (results) => {
          this.router.navigate(["/risks", this.riskId, "questionnaires", this.questionnaireId, "completed"], {
            state: {
              allQuestionnaires: this.allQuestionnaires,
              currentIndex: this.currentQuestionnaireIndex,
              hasNext: this.hasNextQuestionnaire,
              nextQuestionnaireId: this.nextQuestionnaireId,
              riskId: this.riskId,
              score: results.score,
              interpretation: results.interpretation,
              nextSteps: results.nextSteps,
              dna: results.dna,
            },
          });
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
        },
      });
  }

  /**
   * Retrieves the available coding choices for a questionnaire question.
   * @param question Question whose choices should be retrieved.
   * @return Array of available coding options.
   */
  getChoices(question: IQuestion): ICoding[] {
    if (!question.options?.reference) return [];

    const valueSet = this.questionnaire.contained.find((c) => c.idSet === question.options.reference);

    return valueSet?.compose.include[0].concept || [];
  }

  /**
   * Indicates whether another questionnaire exists after the current one.
   * @return True if another questionnaire is available; otherwise false.
   */
  get hasNextQuestionnaire(): boolean {
    return this.currentQuestionnaireIndex < this.allQuestionnaires.length - 1;
  }

  /**
   * Gets the identifier of the next questionnaire if available.
   * @return Identifier of the next questionnaire or null if none exists.
   */
  get nextQuestionnaireId(): string | null {
    return this.hasNextQuestionnaire ? this.allQuestionnaires[this.currentQuestionnaireIndex + 1] : null;
  }
}
